import React from 'react';

import { Box, Button, ButtonGroup, Heading, Paragraph, Text } from '@zillow/constellation-10';

import Page from '@renter-hub/components/RenterPage';
import NotificationContainer from '@common/components/NotificationContainer';
import DebouncedButton from '@common/components/ConstellationDebouncedButton';
import FaqComponent from '@common/components/ConstellationFaqComponent';
import HelmetContainer from '@common/components/HelmetContainer';
import TotalPaymentModule from '@renter-hub/components/TotalPaymentModule';
import SeoConstants from '@renter-hub/constants/seoConstants';
import userPersonas from '@common/constants/userPersonas';
import faqs from '@common/constants/faqs';
import { centsToDollars } from '@common/paymentsUtils/currencyUtils';
import { capitalizeFirstLetter } from '@common/utils/stringUtils';
import type { RenterPaymentMethodInterface } from '@renter-hub/models/Types';
import AvailablePaymentMethods from '../../components/AvailablePaymentMethods';
import RachelCSEvents from '@renter-hub/tracking/ClickStreamEvents';
import type { ZrmClickstreamUser } from '@zg-rentals/clickstream';

interface PayOneTimePaymentPageProps {
  transaction: {
    amount: number;
    totalAmount: number;
    zgFee: number;
    paymentType: string;
    notes?: string;
    paymentPlanInstallmentTitle?: string;
  };
  accountLast4: string;
  accountName: string;
  landlordName: string;
  landlordFirstName: string;
  onEditPaymentInfo: () => void;
  goToDashboard: () => void;
  onSubmit: (...args: Array<any>) => any;
  failedPaymentEmail: boolean;
  paymentFailureReason: string;
  paymentMethods: Array<RenterPaymentMethodInterface>;
  groupKey: string;
  user: ZrmClickstreamUser;
}

interface PayOneTimePaymentPageState {
  isLoading: boolean;
  failedPayment: boolean;
  paymentFailureReason: string;
  selectedPaymentMethodId: string;
}

class PayOneTimePaymentPage extends React.Component<PayOneTimePaymentPageProps, PayOneTimePaymentPageState> {
  constructor(props: PayOneTimePaymentPageProps) {
    super(props);
    this.state = {
      isLoading: false,
      failedPayment: props.failedPaymentEmail || false,
      paymentFailureReason: props.paymentFailureReason || '',
      selectedPaymentMethodId: this.props.paymentMethods?.length > 0 ? this.props.paymentMethods[0]?.id || '' : '',
    };
  }

  renderTotalPaymentModule = () => {
    const detailsOfSelectedPaymentMethod = this.props.paymentMethods.find(
      (paymentMethod) => paymentMethod.id === this.state.selectedPaymentMethodId,
    );

    const oneTimePaymentAmount = detailsOfSelectedPaymentMethod?.oneTimePaymentAmountDetails?.find(
      (oneTimeDetails) => oneTimeDetails.groupKey === this.props.groupKey,
    );

    if (oneTimePaymentAmount) {
      return <TotalPaymentModule totalAmount={oneTimePaymentAmount.totalAmount} feeAmount={oneTimePaymentAmount.fee} />;
    }

    const { totalAmount, zgFee } = this.props.transaction;
    if (Number(zgFee) === 0) {
      return null;
    }
    return (
      <Box>
        <TotalPaymentModule totalAmount={totalAmount} feeAmount={zgFee} />
      </Box>
    );
  };

  onPaymentMethodChange = (e): void => {
    this.setState({ selectedPaymentMethodId: e.target.value });
  };

  renderButtons = () => {
    if (this.props.failedPaymentEmail) {
      return (
        <>
          <DebouncedButton
            emphasis="filled"
            size="lg"
            fluid={true}
            onClick={this.onSubmit}
            loading={this.state.isLoading}
          >
            Try again
          </DebouncedButton>
          <Paragraph textStyle="body-sm" css={{ textAlign: 'center', color: 'text.subtle' }}>
            Your payment will be processed immediately
          </Paragraph>
        </>
      );
    }
    return (
      <>
        <ButtonGroup aria-label="confirm-mark-paid" role="group">
          <Button size="lg" fluid={true} onClick={this.props.goToDashboard}>
            Back
          </Button>
          <DebouncedButton
            emphasis="filled"
            size="lg"
            fluid={true}
            onClick={this.onSubmit}
            loading={this.state.isLoading}
          >
            {this.state.failedPayment ? 'Try again' : 'Send now'}
          </DebouncedButton>
        </ButtonGroup>
        <Paragraph textStyle="body-sm" css={{ color: 'text.subtle', textAlign: 'center' }}>
          Your payment will be processed immediately
        </Paragraph>
      </>
    );
  };

  onSubmit = async (): Promise<void> => {
    this.setState({ isLoading: true });
    const { selectedPaymentMethodId } = this.state;

    RachelCSEvents.emitClicksSendNowForOTPEvent({ user: this.props.user });

    const response = await this.props.onSubmit(selectedPaymentMethodId);
    const { error, failedPayment, paymentFailureReason } = response;

    if (error) {
      this.setState({ failedPayment, paymentFailureReason, isLoading: false });
    }
  };

  render() {
    return (
      <Page>
        <HelmetContainer {...SeoConstants.payOneTimePaymentPage} />
        <Page.MainSection>
          <Box css={{ display: 'flex', flexDirection: 'column', gap: 'token(spacing.md)' }}>
            <NotificationContainer persona={userPersonas.RENTER} />
            <Heading level={1} textStyle="heading-sm">
              {this.props.landlordFirstName} has requested a payment
            </Heading>
            <Box>
              <Paragraph textStyle="body-bold">Amount</Paragraph>
              <Paragraph>{centsToDollars(this.props.transaction.amount, { style: 'effectiveShort' })}</Paragraph>
            </Box>
            <Box>
              <Paragraph textStyle="body-bold">For</Paragraph>
              <Paragraph>
                {this.props.transaction.paymentPlanInstallmentTitle ||
                  capitalizeFirstLetter(this.props.transaction.paymentType)}
              </Paragraph>
              {this.props.transaction.notes && <Paragraph>{this.props.transaction.notes}</Paragraph>}
            </Box>
            <Box>
              <Paragraph textStyle="body-bold">Pay To</Paragraph>
              <Text className="fs-exclude">{this.props.landlordName}</Text>
            </Box>
            <AvailablePaymentMethods
              renderSelect
              onOptionChange={this.onPaymentMethodChange}
              isLoading={this.state.isLoading}
              selectedPaymentMethodId={this.state.selectedPaymentMethodId}
            />

            {this.renderTotalPaymentModule()}
            {this.renderButtons()}
          </Box>
        </Page.MainSection>
        <Page.RightSection>
          <FaqComponent faqs={faqs.ONE_TIME_PAYMENT_RACHEL.body} />
        </Page.RightSection>
        <Page.BottomSection>
          <FaqComponent faqs={faqs.ONE_TIME_PAYMENT_RACHEL.body} />
        </Page.BottomSection>
      </Page>
    );
  }
}

export default PayOneTimePaymentPage;
